import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { Lock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errDetail, toISO } from "@/lib/api";
import type { Block, Yacht } from "@/types";

export default function AdminCalendar() {
  const { data: yachts = [] } = useQuery<Yacht[]>({ queryKey: ["admin-yachts"], queryFn: async () => (await api.get("/admin/yachts")).data });
  const [yachtId, setYachtId] = useState("");
  useEffect(() => { if (!yachtId && yachts.length) setYachtId(yachts[0].id); }, [yachts, yachtId]);
  const { data: blocks = [], refetch } = useQuery<Block[]>({
    queryKey: ["admin-blocks", yachtId], enabled: !!yachtId,
    queryFn: async () => (await api.get(`/admin/yachts/${yachtId}/blocks`)).data,
  });
  const byDate = new Map(blocks.map((b) => [b.date, b]));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const upcoming = blocks.filter((b) => b.date >= toISO(today));

  const toggle = async (day: Date) => {
    const iso = toISO(day);
    const b = byDate.get(iso);
    try {
      if (!b) {
        await api.post("/admin/blocks", { yacht_id: yachtId, date: iso, note: "Bloqueo manual" });
        toast.success(`${iso} bloqueado`);
      } else if (b.source === "manual") {
        await api.delete(`/admin/blocks/${yachtId}/${iso}`);
        toast.success(`${iso} liberado`);
      } else {
        toast.info("Esta fecha está ligada a una reserva. Cancélala desde Reservas para liberarla.");
      }
    } catch (e) {
      toast.error(errDetail(e));
    }
    refetch();
  };

  const release = async (b: Block) => {
    await api.delete(`/admin/blocks/${b.yacht_id}/${b.date}`);
    toast.success(`${b.date} liberado`);
    refetch();
  };

  return (
    <div className="max-w-6xl" data-testid="admin-calendar-page">
      <h1 className="font-display text-3xl sm:text-4xl text-[#0B192C]">Calendario de disponibilidad</h1>
      <p className="mt-2 text-sm text-slate-500">Toca un día para bloquearlo o liberarlo manualmente. Los días con reserva pagada o confirmada aparecen en azul marino.</p>
      <div className="mt-6 max-w-sm">
        <Select value={yachtId} onValueChange={setYachtId}>
          <SelectTrigger data-testid="admin-calendar-yacht-select"><SelectValue placeholder="Elige un yate" /></SelectTrigger>
          <SelectContent>{yachts.map((y) => <SelectItem key={y.id} value={y.id} data-testid={`admin-calendar-yacht-${y.id}`}>{y.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="mt-6 grid gap-8 lg:grid-cols-[auto_1fr] items-start">
        <div className="bg-white rounded-lg border border-slate-200 p-2 overflow-x-auto">
          <Calendar mode="single" numberOfMonths={2} selected={undefined} onDayClick={toggle} disabled={{ before: today }} data-testid="admin-calendar"
            modifiers={{ booked: blocks.filter((b) => b.source === "booking").map((b) => parseISO(b.date)), manual: blocks.filter((b) => b.source === "manual").map((b) => parseISO(b.date)) }}
            modifiersClassNames={{ booked: "day-booked", manual: "day-manual" }} />
          <div className="px-3 pb-2 flex gap-5 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><i className="h-3 w-3 rounded-sm bg-red-100 border border-red-300" />Bloqueo manual</span>
            <span className="flex items-center gap-1.5"><i className="h-3 w-3 rounded-sm bg-[#0B192C]" />Reserva</span>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <p className="font-semibold text-[#0B192C]">Próximas fechas bloqueadas ({upcoming.length})</p>
          <ul className="mt-4 divide-y divide-slate-100" data-testid="admin-blocks-list">
            {upcoming.length === 0 && <li className="py-3 text-sm text-slate-500">Sin bloqueos.</li>}
            {upcoming.map((b) => (
              <li key={b.id} className="py-3 flex items-center justify-between gap-3 text-sm" data-testid={`admin-block-${b.date}`}>
                <span><b>{b.date}</b> <span className="text-slate-500">· {b.source === "booking" ? b.note || "Reserva" : "Manual"}</span></span>
                {b.source === "manual" ? (
                  <button onClick={() => release(b)} data-testid={`admin-block-release-${b.date}`} className="inline-flex items-center gap-1 text-red-600 hover:underline"><Trash2 className="h-3.5 w-3.5" />Liberar</button>
                ) : <Lock className="h-4 w-4 text-slate-400" />}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
